import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { useLocation } from "react-router-dom";

import SearchBox from "src/Components/SearchBox";
import UserService from "src/Services/API/user.service";
import { getAvatarUrl, getImageUrl } from "src/Common/common";
import { getActingTenant } from "src/Common/tenant";
import { term, termPlural } from "src/Common/vocab";
import AuthService from "src/Services/API/auth.service";
import { setAuthState } from "src/Redux/Slices/AuthSlice";

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuShown, setMenuShown] = useState(false);
  const [title, setTitle] = useState("");

  const woNumber = useSelector((state) => state.Header.woNumber);
  const poNumber = useSelector((state) => state.Header.poNumber);
  const customerName = useSelector((state) => state.Header.customerName);
  const ticketNumber = useSelector((state) => state.Header.ticketNumber);

  // Read from the stored user on every render (not one-shot state): the app
  // root re-fetches /me on reload and bumps a version to re-render, and the
  // header must pick up the refreshed name/role/avatar without a remount.
  const currentUser = UserService.getCurrentUser();
  const avatar = currentUser?.avatar ? getAvatarUrl(currentUser.avatar) : null;
  const name = currentUser?.name ?? "";
  const role = currentUser?.roles?.[0]?.name ?? "";
  // A Super Admin who hasn't entered a tenant is confined to the Platform view,
  // so the settings link (a tenant page) is hidden rather than left as a link
  // that just bounces back.
  const inPlatformMode = role === "Super Admin" && !getActingTenant();

  const settingClicked = () => {
    navigate("/settings/account");
  };

  const signOut = () => {
    dispatch(setAuthState(false));
    AuthService.logout();
    navigate("/");
  };

  useEffect(() => {
    if (location.pathname.includes("/work/")) {
      // Requested WOs have no number until they are approved.
      const hasWoNumber = woNumber && woNumber !== "null" && woNumber !== "undefined";
      setTitle(hasWoNumber ? `Work Order # ${woNumber}` : "Work Order # Unassigned");
    } else if (location.pathname.includes("/work")) {
      setTitle(termPlural("workOrder"));
    } else if (location.pathname.includes("/purchase/")) {
      setTitle("Purchase Order #" + poNumber);
    } else if (location.pathname.includes("/purchase")) {
      setTitle("Purchase Orders");
    } else if (location.pathname.includes("/recommendations")) {
      setTitle(termPlural("recommendation"));
    } else if (location.pathname.includes("/roles")) {
      setTitle("Roles");
    } else if (location.pathname.includes("/policies")) {
      setTitle("Policies");
    } else if (location.pathname.includes("/team")) {
      setTitle("Team");
    } else if (location.pathname.includes("/setting")) {
      setTitle("Settings");
    } else if (location.pathname.includes("/calendar")) {
      setTitle("Calendar");
    } else if (location.pathname.includes("/location/")) {
      setTitle(`${customerName}`);
    } else if (location.pathname.includes("/report")) {
      setTitle("Report");
    } else if (location.pathname.includes("/customer")) {
      if (location.pathname.includes("/customer/")) {
        setTitle(`${customerName}`);
      } else {
        setTitle("Customers");
      }
    } else if (location.pathname.includes("/time-off")) {
      setTitle("Time Off Requests");
    } else if (location.pathname.includes("/my-time-off")) {
      setTitle("My Time Off");
    } else if (location.pathname.includes("/userguides")) {
      setTitle("User Guides");
    } else if (location.pathname.includes("/ticket/new")) {
      setTitle("New Ticket");
    } else if (location.pathname.includes("/ticket/detail")) {
      setTitle(ticketNumber ? `Ticket #${ticketNumber}` : "Ticket");
    } else if (location.pathname.includes("/ticket")) {
      const currentUser = UserService.getCurrentUser();
      const isSuperAdmin = currentUser?.roles?.[0]?.name === "Super Admin";
      setTitle(isSuperAdmin ? "Ticket Management" : "My Tickets");
    } else if (location.pathname.includes("/serviceticket")) {
      setTitle("Service Ticket Editor");
    } else if (location.pathname.includes("/form-templates")) {
      setTitle("Templates");
    } else if (location.pathname.includes("/all-forms")) {
      setTitle("All Forms");
    } else if (location.pathname.includes("/form-builder")) {
      setTitle("Makani Form Builder");
    } else if (location.pathname.includes("/platform/app-versions")) {
      setTitle("App Versions");
    } else if (location.pathname.includes("/platform") || location.pathname.includes("/companies")) {
      setTitle("Tenants");
    }
  }, [location, customerName, woNumber, poNumber, ticketNumber]);

  return (
    <div className="w-full">
      <div className="border-gray-100 border-[1px] rounded-md flex items-center justify-between w-full h-[60px] px-5 relative">
        <div className="flex flex-col">
          <span data-testid="page-title" className="font-OpenSansRegular font-[700] text-[24px] text-titleColor">{title}</span>
        </div>

        <div className="flex items-center gap-2">
          <SearchBox />
          {!inPlatformMode && (
            <img
              onClick={settingClicked}
              src={getImageUrl("/images/common/setting.png")}
              className="w-[13px] cursor-pointer"
            />
          )}
          <div onClick={() => setMenuShown(true)} className="flex items-center gap-2 cursor-pointer relative">
            <img src={avatar} className="w-[20px] h-[20px] object-contain rounded-[10px] ml-2" />
            <div className="flex flex-col items-center">
              <span data-testid="header-user-name" className="font-OpenSansRegular font-[400] text-[10px] text-subTitleColor">{name}</span>
              <span className="font-OpenSansRegular font-[400] text-[9px] text-subTitleColor">{role}</span>
            </div>

            <OutsideClickHandler
              onOutsideClick={() => {
                setMenuShown(false);
              }}
            >
              {menuShown && (
                <div
                  onClick={signOut}
                  className="flex flex-col items-center py-1 justify-between absolute top-[25px] left-[8px] w-[60px] h-[26px] border-[1px] rounded-[6px] border-gray-100 bg-white z-10"
                >
                  <span data-testid="header-logout" className="font-OpenSansMedium text-[13px] text-black">Log out</span>
                </div>
              )}
            </OutsideClickHandler>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
